import { useRef, useState, useCallback, useEffect } from 'react'

function useLiquidGlassInteractive() {
  const ref = useRef(null)
  const rafRef = useRef(null)
  const [pressing, setPressing] = useState(false)
  const [hovering, setHovering] = useState(false)
  const [shimmer, setShimmer] = useState(false)

  const updatePointer = useCallback((clientX, clientY) => {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const x = clientX - rect.left
    const y = clientY - rect.top
    const nx = x / rect.width - 0.5
    const ny = y / rect.height - 0.5
    const angle = Math.atan2(ny, nx) * (180 / Math.PI) + 90

    el.style.setProperty('--mx', `${x}px`)
    el.style.setProperty('--my', `${y}px`)
    el.style.setProperty('--mx-pct', `${(x / rect.width) * 100}%`)
    el.style.setProperty('--my-pct', `${(y / rect.height) * 100}%`)
    el.style.setProperty('--light-angle', `${angle}deg`)
  }, [])

  const onPointerMove = useCallback(
    (e) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(() => updatePointer(e.clientX, e.clientY))
    },
    [updatePointer]
  )

  const onPointerEnter = useCallback(
    (e) => {
      setHovering(true)
      document.documentElement.classList.add('over-glass')
      updatePointer(e.clientX, e.clientY)
    },
    [updatePointer]
  )

  const onPointerLeave = useCallback(() => {
    setHovering(false)
    setPressing(false)
    document.documentElement.classList.remove('over-glass')
  }, [])

  const onPointerDown = useCallback(
    (e) => {
      setPressing(true)
      setShimmer(true)
      updatePointer(e.clientX, e.clientY)
    },
    [updatePointer]
  )

  const onPointerUp = useCallback(() => {
    setPressing(false)
  }, [])

  const onShimmerEnd = useCallback(() => {
    setShimmer(false)
  }, [])

  useEffect(() => {
    const root = document.documentElement
    let spotlightRaf = null
    let lastEvent = null

    const flushSpotlight = () => {
      spotlightRaf = null
      if (!lastEvent) return
      root.style.setProperty('--cx', `${lastEvent.clientX}px`)
      root.style.setProperty('--cy', `${lastEvent.clientY}px`)
    }

    const onMouseMove = (e) => {
      lastEvent = e
      if (spotlightRaf === null) spotlightRaf = requestAnimationFrame(flushSpotlight)
    }

    document.addEventListener('mousemove', onMouseMove, { passive: true })
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      root.classList.remove('over-glass')
      if (spotlightRaf !== null) cancelAnimationFrame(spotlightRaf)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  return {
    ref,
    pressing,
    hovering,
    shimmer,
    onShimmerEnd,
    handlers: {
      onPointerMove,
      onPointerEnter,
      onPointerLeave,
      onPointerDown,
      onPointerUp,
      onPointerCancel: onPointerUp
    }
  }
}

export default function LiquidGlassText({ text = 'En mantenimiento' }) {
  const { ref, pressing, hovering, shimmer, onShimmerEnd, handlers } = useLiquidGlassInteractive()

  return (
    <>
      <svg className="liquid-glass-svg" aria-hidden="true">
        <defs>
          <filter id="lg-refract" x="-30%" y="-30%" width="160%" height="160%" colorInterpolationFilters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.012 0.018" numOctaves="2" seed="3" result="noise">
              <animate attributeName="baseFrequency" dur="12s" values="0.012 0.018;0.014 0.016;0.012 0.018" repeatCount="indefinite" />
            </feTurbulence>
            <feGaussianBlur in="noise" stdDeviation="1.2" result="smoothNoise" />
            <feDisplacementMap in="SourceGraphic" in2="smoothNoise" scale="8" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
      </svg>

      <div className="lg-spotlight" aria-hidden="true" />

      <div className="liquid-glass-overlay" role="status" aria-live="polite">
        <div
          ref={ref}
          className={[
            'liquid-glass',
            'liquid-glass-pill',
            'lg-interactive',
            hovering && 'is-hovering',
            pressing && 'is-pressing',
            shimmer && 'is-shimmering'
          ]
            .filter(Boolean)
            .join(' ')}
          tabIndex={0}
          {...handlers}
        >
          <div className="liquid-glass-effect" />
          <div className="liquid-glass-tint" />
          <div className="liquid-glass-shine" />
          <div className="liquid-glass-rim" aria-hidden="true" />
          <div className="liquid-glass-illumination" aria-hidden="true" />
          <div className="liquid-glass-shimmer" onAnimationEnd={onShimmerEnd} aria-hidden="true" />
          <span className="liquid-glass-content liquid-glass-text">{text}</span>
        </div>
      </div>
    </>
  )
}
